import { Box, Container, Stack, Card, Grid, SvgIcon, TextField, Button, Typography, CardContent, Tooltip, Divider, useTheme, Backdrop, CircularProgress } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Layout from "../../components/layout/Layout";
import Edit02Icon from "@untitled-ui/icons-react/build/esm/Edit02";
import { emailTempSave, getEmailTemp, editEmailTemp, emailFlagOff } from "../../redux/actions/EmailAction";
import { useSettings } from "src/hooks/use-settings";
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddProductDialog from "./components/AddProductDialog";
import { attachProductToPlan, createPrice, createProduct, editPrice, editProduct, getPlanPrices, resetProductsData, updatePlanProductsIds } from "src/redux/actions/Products";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import toast from "react-hot-toast";
import { api } from "src/api/endpoints";
import AddPriceDialog from "./components/AddPriceDialog";
import ConfirmDisableDialog from "./components/ConfirmDisableDialog";
import ConfirmDisableProductDialog from "./components/ConfirmDisableProductDialog";
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { parsePrice } from "src/utils/parse-price";
import SetIdsDialog from "./components/SetIdsDialog";
import IdsChangeConfirmDialog from "./components/IdsChangeConfirmDialog";
import ChangePricesDialog from "./components/ChangePricesDialog";
import ChangePricesConfirmDialog from "./components/ChangePricesConfimDialog";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

function ProductsManager({ user }) {
    const { t } = useTranslation();
    const settings = useSettings();
    const dispatch = useDispatch()
    const isDarkTheme = useTheme().palette.mode === 'dark';

    const { plans, additionalFeatures } = useSelector((state) => state.Plan);
    const { products } = useSelector((state) => state.Products)

    const [isIdsDialogOpen, setIsIdsDialogOpen] = useState(false)
    const [idsData, setIdsData] = useState(null)
    const [idsWithOldData, setIdsWithOldData] = useState(null)
    const [isConfirmIdsDialogOpen, setIsConfirmIdsDialogOpen] = useState(false)
    const [isChangePricesDialogOpen, setIsChangePricesDialogOpen] = useState(false)
    const [pricesToChange, setPricesToChange] = useState([])
    const [pricesToConfirm, setPricesToConfirm] = useState([])
    const [isConfirmPricesDialogOpen, setIsConfirmPricesDialogOpen] = useState(false)
    const [currentChangePriceProduct, setCurrentChangePriceProduct] = useState('')
    const [loadingBackdrop, setLoadingBackdrop] = useState(false)

    const updateDBproductsIds = () => {
        dispatch(updatePlanProductsIds(idsData))
    }

    useEffect(() => {
      if(plans.concat(additionalFeatures).length > 0){
        const idsArr = []
        plans.concat(additionalFeatures).forEach(p => {
          if(p.productId){
            if(!products.find(pr => pr.productId == p.productId)){
              idsArr.push(p.productId)
            } 
          }
        })
        if(idsArr.length > 0) {
          dispatch(getPlanPrices(idsArr))
        }
        setIsConfirmIdsDialogOpen(false)
      }
    }, [plans])

    const handleConfirmIdsChange = (data) => {
        const idsWithOld = data.map(id => {
            return {
                ...id,
                old: plans.concat(additionalFeatures).find(p => p._id == id.id) ? (plans.concat(additionalFeatures).find(p => p._id == id.id).productId ? plans.concat(additionalFeatures).find(p => p._id == id.id).productId : '') : '',
                new: id.value
            }
        })
        setIdsData(data)
        setIdsWithOldData(idsWithOld)
        setIsIdsDialogOpen(false)
        setIsConfirmIdsDialogOpen(true)
    }

    const handleChangePricesDialogOpen = (productId) => {
      setCurrentChangePriceProduct(productId)
      setPricesToChange(products.find(p => p.productId == productId).prices)
      setIsChangePricesDialogOpen(true)
    }

    const handleConfirmChangePricesDialogOpen = (prices) => {
      setIsChangePricesDialogOpen(false)
      const pricesToSet = prices.map(p => {
        return {
          ...p,
          new: p.value,
          old: pricesToChange.find(pr => pr.recurring.interval == p.id)?.unit_amount,
          productId: pricesToChange.find(pr => pr.recurring.interval == p.id)?.product
        }
      })
      setPricesToConfirm(pricesToSet)
      setIsConfirmPricesDialogOpen(true)
    }

    const handleSavePrices = async () => {
      setIsConfirmPricesDialogOpen(false)
      setLoadingBackdrop(true)
      try {
        await api.post('/products/updatePrices', {
          productId: currentChangePriceProduct,
          newMonthPrice: Number(pricesToConfirm.find(p => p.id == 'month')?.value),
          newYearPrice: Number(pricesToConfirm.find(p => p.id == 'year')?.value)
        })
        toast.success("Successfully updated prices")
        setLoadingBackdrop(false)
      } catch (err) {
        toast.error("Error when updating prices")
        setLoadingBackdrop(false)
      }
    }

    const handleBackIdChange = () => {
      setIsIdsDialogOpen(true)
      setIsConfirmIdsDialogOpen(false)
    }

    const handleBackPricesChange = () => {
      setIsChangePricesDialogOpen(true)
      setIsConfirmPricesDialogOpen(false)
    }

    const refreshPlans = () => {
      if(plans.concat(additionalFeatures)?.length > 0){
        dispatch(resetProductsData())
        const idsArr = []
        plans.concat(additionalFeatures).forEach(p => {
          if(p.productId){
            idsArr.push(p.productId)
          }
        })
        dispatch(getPlanPrices(idsArr))
        setIsConfirmIdsDialogOpen(false)
      }
    }

    const handlePdfInstructionsDownload = async () => {
      await api.get('/products/downloadStripeInstructions')
    }

  return (
    <>
      <Layout title={t("views.redesignEmail.title")} user={user}>
        <Backdrop
          sx={(theme) => ({zIndex: theme.zIndex.drawer + 1 })}
          open={loadingBackdrop}
        >
          <CircularProgress/>
        </Backdrop>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 2,
          }}>
          <Container maxWidth={settings.stretch ? false : "xl"}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '20px',
              maxWidth: 1500,
              margin: '0 auto',
                mb: 5,
            }}>
              <Button variant="contained" onClick={() => setIsIdsDialogOpen(true)}>{t("views.productsManager.setProductsIds")}</Button>

              {/* <Tooltip title="Each change in stripe can take up to 1 minute" placeholder="bottom">
                <InfoOutlinedIcon color="info" />
              </Tooltip> */}
              
              <Typography color="warning.main">{t("views.productsManager.eachChangeInfo")}</Typography>

              <a href={`${process.env.REACT_APP_BACKEND_URL}api/products/downloadStripeInstructions`} target="_blank" rel="noopener noreferrer">
                <Button endIcon={<PictureAsPdfIcon />} onClick={handlePdfInstructionsDownload}>Download PDF instructions</Button>
              </a>
              
              <Box sx={{
                display: "flex",
                gap: "15px",
                alignItems: "center"
              }}>
                <Button onClick={refreshPlans} startIcon={<RefreshOutlinedIcon/>}>{t("views.productsManager.refresh")}</Button>
                {plans.concat(additionalFeatures).filter(p => p.productId).length !== products.length ? (
                  <CircularProgress size="25px" />
                ) : null}
              </Box>
              {/* {plans.concat(additionalFeatures).filter(p => p.productId).length == products.length ? (
                <Button onClick={refreshPlans} startIcon={<RefreshOutlinedIcon/>}>{t("views.productsManager.refresh")}</Button>
              ) : (
                <CircularProgress size="25px" />
              )} */}
            </Box>

            <Box maxWidth={1000} mx="auto" sx={{
                display: 'flex',
                gap: '20px',
                justifyContent:'center',
                flexWrap: 'wrap'
            }}>
                {plans.concat(additionalFeatures).map(p => {
                      return (
                        !p.hidden && p.paid ? (
                          <Card variant="outlined" sx={{
                              width: '300px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between'
                          }}>
                            <Box>
                              <Box sx={{
                                backgroundColor: isDarkTheme ? '#1C2536' : '#F8F9FA',
                                p:2
                              }}>
                                <Typography variant="button">{p.planType == 'plan' ? 'Plan' : p.planType == 'additional-feature' ? t("views.productsManager.additionalFeature") : ''}</Typography>
                                <Typography variant="h5">{p.name}</Typography>
                              </Box>
                              <Divider />
                              <TableContainer sx={{
                                p: 2
                              }}>
                                <Table>
                                  <TableBody>
                                    {products.find(pr => pr.productId == p.productId) ? (
                                      products.find(pr => pr.productId == p.productId)?.prices.length > 0 ? (
                                        products.find(pr => pr.productId == p.productId)?.prices.map(pri => {
                                          return (
                                            <TableRow key={pri.id}>
                                              <TableCell>{pri.recurring.interval} {pri.linkedProductData ? plans.concat(additionalFeatures).find(dbp => dbp.productId == pri.linkedProductData.id) ? '('+plans.concat(additionalFeatures).find(dbp => dbp.productId == pri.linkedProductData.id).name+')' : '' : ''}</TableCell>
                                              <TableCell>{parsePrice(pri.unit_amount)}</TableCell>
                                            </TableRow>
                                          )
                                        })
                                      ) : (
                                        <Typography>{t("views.productsManager.noPricesInfo")}</Typography>
                                      )
                                    ) : null}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                            <Box sx={{
                              p: 2
                            }}>
                              <Button variant="contained" disabled onClick={() => handleChangePricesDialogOpen(p.productId)} fullWidth>{t("views.productsManager.changePrices")}</Button>
                            </Box>
                          </Card>
                        ) : null
                    )
                })}
            </Box>
            <ChangePricesDialog open={isChangePricesDialogOpen} handleClose={() => setIsChangePricesDialogOpen(false)} prices={pricesToChange} onSubmit={handleConfirmChangePricesDialogOpen}/>
            <ChangePricesConfirmDialog prices={pricesToConfirm} open={isConfirmPricesDialogOpen} handleClose={() => setIsConfirmPricesDialogOpen(false)} handlePricesSave={handleSavePrices} handleBack={handleBackPricesChange} />
            <SetIdsDialog open={isIdsDialogOpen} handleClose={() => setIsIdsDialogOpen(false)} plans={plans.concat(additionalFeatures)} onSubmit={handleConfirmIdsChange}/>
            <IdsChangeConfirmDialog open={isConfirmIdsDialogOpen} handleClose={() => setIsConfirmIdsDialogOpen(false)} ids={idsWithOldData} handleIDsave={updateDBproductsIds} handleBack={handleBackIdChange} />
          </Container>
        </Box>
      </Layout>
    </>
  );
}

export default ProductsManager;
